import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';

import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import { Utilities } from '../../../services/utilities';

import { Roles } from '../../../models/enums';
import { Role } from '../../../models/role.model';
import { UserEdit } from '../../../models/user-edit.model';
import { LoginConfig } from '../../../models/login-config.model';

import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MustMatchDirective } from '../../../helpers/must-match.directive';
import { MustMatch2Directive } from '../../../helpers/must-match2.directive';
import { PasswordStrengthDirective } from '../../../helpers/password-strength.directive'
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { ArraySortPipe } from '../../../pipes/sort.pipe';

@Component({
  selector: 'app-account-admin-edit',
  templateUrl: './account-admin-edit.component.html',
  styleUrls: ['./account-admin-edit.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatRadioModule,
    MustMatchDirective,
    MustMatch2Directive,
    PasswordStrengthDirective,
    ReactiveFormsModule,
    RouterModule,
    ArraySortPipe
  ]
})
export class AccountAdminEditComponent implements OnInit {
  user: UserEdit = new UserEdit();
  userIdParam: string;
  confirmEmail: string = "";
  roles: Role[] = [];
  loginStatusSubscription: any;
  formResetToggle: boolean = true;
  hidePasswordFields: boolean = false;
  isLocked: boolean = false;
  isAdmin: boolean = false;
  isAdminForSelf: boolean = false;
  isSelf: boolean = false;
  isActiveForRadioButtonClass: boolean = false;
  showValidationErrors: boolean = false;
  showSuccess: boolean = false;
  showFailure: boolean = false;
  roleLabels: any[] = [];
  loginConfiguration: LoginConfig;

  @ViewChild('f') private form: NgForm;

  constructor(
    private alertService: AlertService,
    private accountService: AccountService,
    private authService: AuthService,
    private router: Router,
    private activeRoute: ActivatedRoute
  ) {
    // Force route reload whenever params change
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnInit(): void {
    if (this.authService.isSessionExpired) {
      this.router.navigateByUrl('/account/login');
    } else {
      this.userIdParam = this.activeRoute.snapshot.params['id'];
      this.isAdmin = this.accountService.IsAdminRole();
    }

    if (!this.userIdParam && !this.isAdmin) {
      this.alertService.showMessage("Unauthorized", "User lacks permissions.", MessageSeverity.error);
      this.router.navigateByUrl('/home/actions');
    }

    if (!this.userIdParam && this.isAdmin) {
      this.user.isEnabled = false;
      this.isActiveForRadioButtonClass = true;
    }

    if (this.isAdmin && this.userIdParam == this.accountService.GetCurrentLoggedUserId()) {
      this.isAdminForSelf = true;
    }

    if (this.userIdParam == 'me') {
      this.isSelf = true;
    }

    if (this.isAdmin) {
      this.loadRoles();
      this.loadLoginConfiguration();
    }

    if (this.userIdParam) {
      this.loadData(this.userIdParam);
    }
  }
  
  resetUserPassword() {
    this.hidePasswordFields = false;
  }

  ngOnDestroy() {
    if (this.loginStatusSubscription) this.loginStatusSubscription.unsubscribe();
  }

  showErrorAlert(caption: string, message: string) {
    this.alertService.showMessage(caption, message, MessageSeverity.error);
  }

  resetForm(replace = false) {
    if (!replace) {
      this.form.reset();
    } else {
      this.formResetToggle = false;
      setTimeout(() => (this.formResetToggle = true));
    }
  }

  loadData(userId: string) {
    this.hidePasswordFields = true;
    const getUser$ = userId === 'me'
      ? this.accountService.getUserSelf()
      : this.accountService.getUser(userId);

    getUser$.subscribe(
      (user) => {
        Object.assign(this.user, user);
        this.user.confirmEmail = user.email;
        this.isLocked = user.isLockedOut;
        this.roleLabels = this.accountService.getRoleLabels(this.user.roles);
      },
      (error) => {
        this.authService.logout();
        this.authService.redirectLogoutUser();
        this.alertService.showStickyMessage(
          'Load Error', `Error retrieving user: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error, error
        );
      }
    );
  }

  loadRoles() {
    this.accountService.getRoles().subscribe(
      (roles) => (this.roles = roles),
      (error) =>
        this.alertService.showStickyMessage(
          "Load Error",
          `Error retrieving roles: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error,
          error
        )
    );
  }

  loadLoginConfiguration() {
    this.accountService.getLoginConfiguration().subscribe(
      (config) => (this.loginConfiguration = config),
      (error) =>
        this.alertService.showStickyMessage(
          "Load Error",
          `Error retrieving login configuration: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error,
          error
        )
    );
  }

  enableUser(enable: boolean) {
    this.user.isEnabled = enable;
  }

  addRemoveRole(role: string) {
    if (this.user.roles.includes(role)) {
      this.user.roles = this.user.roles.filter((r) => r !== role);
    } else {
      if (role === "Virtual Coder" || this.user.roles.includes("Virtual Coder")) {
        this.user.roles = [];
      }
      this.user.roles.push(role);
    }
  }

  validateForm(form: NgForm) {
    console.log(form);
    if (form.form.valid) {
      this.upSertUser();
      // TO DO: check what this was supposed to be. Function is not declared here.
      return;
    }

    const controls = form.controls;

    if (controls['firstName'] && !controls['firstName'].valid) {
      this.showErrorAlert('First Name is required.', 'Please enter a valid first name.');
    }
    if (controls['lastName'] && !controls['lastName'].valid) {
      this.showErrorAlert('Last Name is required.', 'Please enter a valid last name.');
    }
    if (controls['email'] && !controls['email'].valid) {
      this.showErrorAlert('Email is required.', 'Please enter a valid email.');
    }
    if (controls['confirmEmail'] && !controls['confirmEmail'].valid) {
      this.showErrorAlert('Confirm Email is incorrect.', 'Please enter a valid confirm email.');
    }
    if (!this.hidePasswordFields && controls['currentPassword'] && !controls['currentPassword'].valid) {
      this.showErrorAlert('Old Password is required.', 'Please enter a valid old password.');
    }
    if (!this.hidePasswordFields && controls['newPassword'] && !controls['newPassword'].valid) {
      this.showErrorAlert('New Password is required.', 'Please enter a valid new password.');
    }
    if (!this.hidePasswordFields && controls['confirmPassword'] && !controls['confirmPassword'].valid) {
      this.showErrorAlert('Confirm Password is incorrect.', 'Please enter a valid confirm password.');
    }
  }

  upSertUser() {
    if (this.user.roles.length < 1) {
      this.alertService.showMessage('Validation Error', 'User must have at least one role selected', MessageSeverity.error);
      return;
    }

    if (!this.user.userName) {
      this.user.userName = this.user.email.split('@').shift();
    }

    if (!this.user.id) {
      /*[Notes:BJL_2023-06-24]: Fix email/username conflict on add user page
      * - Username is set as email prefix if null (above); both username and email must be unique
      * - If admin attempted to add a new user with an existing email, it alerted with "already used" errors for the username and email,
      * but changing the email did not reset the username, and the "already used" error would persist.
      * - I added the line below to always set username to email prefix for new users - i.e. within "if (!this.user.id)".
      * */
      this.user.userName = this.user.email.split('@').shift();
      this.accountService.newUser(this.user)
        .subscribe(user => {
          this.alertService.showMessage('Success', `User \"${user.email}\" was created successfully`, MessageSeverity.success);
          this.showValidationErrors = false;
          this.router.navigateByUrl('/account/confirm/' + user.id);
          this.alertService.stopLoadingMessage();
        }, error => {
          this.alertService.stopLoadingMessage();
          this.alertService.showStickyMessage('Save Error', 'The below errors occured while saving your changes:',
            MessageSeverity.error, error);
          this.alertService.showStickyMessage(error, null, MessageSeverity.error);
        });
    } else {
      this.accountService.updateUser(this.user, this.accountService.GetActiveRole())
        .subscribe(user => {
          this.alertService.showMessage('Success', `User \"${user.email}\" was updated successfully`, MessageSeverity.success);
          this.showValidationErrors = false;


          //if (this.user.isLockedOut) {
          //  this.accountService.unblockUser(this.userIdParam);
          //}

          // TODO: There should be type consistency between the frontend and backend user view models.
          // User view model in the backend returns id as an int, therefore === would return false.
          if (user.id == this.accountService.currentUser.id) {
            this.authService.setUserRoles(user.roles);

            // Route to role select if the current user is no longer an admin.
            if (this.accountService.GetActiveRole() === Roles.Admin && !user.roles.includes('admin')) {
              // TODO: No need to reroute if there is only one role left. Change active role and alert user.
              this.accountService.SetActiveRole(null);
              this.router.navigateByUrl('/account/role/select');
              this.alertService.stopLoadingMessage();
              return;
            }
          }

          const route = '/account/confirm/' + (this.isAdmin ? user.id : 'me');
          this.router.navigateByUrl(route);
          this.alertService.stopLoadingMessage();
        }, error => {
          if (error.url.includes('Account/Login')) {
            this.authService.logout();
            this.authService.redirectLogoutUser();
            this.alertService.showStickyMessage('Load Error',
              `Unable to update user.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
              MessageSeverity.error, error);
          } else {
            this.alertService.stopLoadingMessage();
            this.alertService.showStickyMessage('Save Error', 'The below errors occured while saving your changes:',
              MessageSeverity.error, error);
            this.alertService.showStickyMessage(error, null, MessageSeverity.error);
          }
        });
    }
  }


}

